/* OperationalVisibilityList.jsx — Calm operational visibility surface.
 *
 * Renders a compact, audience-safe list of "what is happening
 * operationally" on a transaction. Designed to live in side rails
 * (Deal Room · Mission Control · client portals) without ever
 * dominating the layout.
 *
 * Doctrine: operational visibility, NOT personal presence.
 *   • No avatars · no online dots · no "viewed" timestamps ·
 *     no "active now" badges · no read receipts.
 *   • Tiny optional timestamp (today / yesterday / Nd ago)
 *     suppressed when `settings.hide_timestamps` is on.
 *
 * Inputs:
 *   • lines   — raw visibility events (`{ kind, at? }[]`).
 *               Caller usually passes lines from
 *               `useOperationalVisibility().filterLines(raw)`.
 *   • title   — optional section title (defaults to
 *               "Operational visibility").
 *   • emptyText — calm fallback when no lines visible to this
 *               audience.
 */
import React from "react";

function relativeFromNow(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 60)       return "today";
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)        return "today";
  const days = Math.floor(hrs / 24);
  if (days === 1)      return "yesterday";
  if (days < 7)        return `${days} days ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export function OperationalVisibilityList({
  lines = [],
  showTime = true,
  title = "Operational visibility",
  emptyText = "No operational activity yet.",
  testid,
}) {
  if (!lines.length) {
    return (
      <section data-testid={testid} className="rounded-2xl border border-slate-100 bg-white p-5 cf-card-hover">
        <h3 className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">{title}</h3>
        <p className="text-[12.5px] text-slate-400 mt-2.5">{emptyText}</p>
      </section>
    );
  }

  return (
    <section
      data-testid={testid}
      className="rounded-2xl border border-slate-100 bg-white p-5 cf-card-hover"
    >
      <h3 className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
        {title}
      </h3>
      <ul className="space-y-2.5">
        {lines.map((l, i) => {
          const when = showTime ? relativeFromNow(l.at) : null;
          return (
            <li
              key={`${l.kind}-${i}`}
              className="flex items-start gap-2.5 leading-snug"
              data-testid={`visibility-line-${l.kind}`}
            >
              <span
                aria-hidden
                className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: "#7C3AED" }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-medium text-slate-800">{l.text}</p>
                {when && (
                  <p className="text-[10.5px] text-slate-400 mt-0.5">{when}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default OperationalVisibilityList;
